import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsService } from 'services/analyticsService';

import { AppContext } from '../../../context/app';
import { useCampusComparison } from '../../../hooks/useCampusComparison';
import { useNavigationManager } from '../../../hooks/useNavigationManager';
import SectionNavigationForm from '../../sections/sectionNavigationForm/SectionNavigationForm';
import AzureMapView from '../../shared/azureMapView/AzureMapView';
import ButtonStyled from '../../shared/button/Button';
import { LegTwoVisualSteps } from '../../shared/LegTwoVisualSteps/LegTwoVisualSteps';
import NavigationDetailsCard from '../../shared/navigationDetailsCard/NavigationDetailsCard';
import { ShareBar } from '../../shared/shareBar/ShareBar';
import TextButton from '../../shared/textButton/TextButton';
import styles from './campusNavigationTemplate.module.scss';

type MobileNavigationTemplateProps = {
  handleNavStepsClose: () => void;
};
export const MobileNavigationTemplate: React.FC<
  MobileNavigationTemplateProps
> = ({ handleNavStepsClose }) => {
  const analyticsService = useMemo(() => getAnalyticsService(), []);
  const { navStartingPlace, navDestinationPlace, directions, legToggle } =
    useContext(AppContext);
  const { t } = useTranslation('common');
  const { isSameCampus } = useCampusComparison();
  const { legTwoRoute, geoNodeIsLoading } = useNavigationManager();

  const [showNavigationRoute, setShowNavigationRoute] =
    useState<boolean>(false);

  const navLocationsExist = useMemo(
    () => !!navStartingPlace && !!navDestinationPlace,
    [navStartingPlace, navDestinationPlace],
  );
  const navigationRoutesAvailable = useMemo(
    () =>
      !!directions ||
      (legTwoRoute?.navSteps?.length !== undefined &&
        legTwoRoute?.navSteps?.length > 0),
    [directions, legTwoRoute?.navSteps?.length],
  );
  const driveTime = useMemo(
    () => Math.round(directions?.travelTime.inSeconds / 60) || null,
    [directions],
  );
  const address = useMemo(
    () =>
      navStartingPlace && 'address' in navStartingPlace
        ? navStartingPlace.address
        : '',
    [navStartingPlace],
  );

  const handleExitNavigation = useCallback(() => {
    setShowNavigationRoute(false);
    handleNavStepsClose();
  }, [handleNavStepsClose]);

  const handleShowNavSteps = () => {
    analyticsService.track(analyticsService.events.ViewDirections, {
      address,
      destinationId: navDestinationPlace?.id,
      driveTime: driveTime,
    });
    setShowNavigationRoute(true);
  };
  return (
    <div className={styles.navParentTemplateMobile}>
      {!showNavigationRoute ? (
        <>
          <SectionNavigationForm />
          {navigationRoutesAvailable && navLocationsExist && (
            <>
              <ShareBar showDriveTime={!isSameCampus} topSeparator />
              <ButtonStyled onClick={() => handleShowNavSteps()} uppercase>
                {t('campusNavigation.form.viewDirections')}
              </ButtonStyled>
            </>
          )}
        </>
      ) : (
        <>
          <div>
            <TextButton
              label={t('campusNavigation.share.backButton')}
              onClick={() => handleExitNavigation()}
              underline
            />
          </div>
          <NavigationDetailsCard />
          {navStartingPlace &&
            !geoNodeIsLoading &&
            !legToggle &&
            !isSameCampus && <AzureMapView />}
          {legTwoRoute && (legToggle || isSameCampus) && (
            <LegTwoVisualSteps onExit={() => handleExitNavigation()} />
          )}
        </>
      )}
    </div>
  );
};
